import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import {
  computeOtherComponentsCost,
  computePartialCosts,
  computePartialCostsComponent,
  computeTotalCost,
} from "../../../../../componentCostUtils";

class ComponentsFormPresenter extends BaseFormPresenter {
  onChange(value, field) {
    this.change[field] = value;
    this.object[field] = value;

    // Preserve raw_materials if present in the view
    if (!this.object.raw_materials && this.view.state.object?.raw_materials) {
      this.object.raw_materials = [...this.view.state.object.raw_materials];
    }
    this.view.setObject(this.object);
  }

  updateItemQuantity(index, stateKey, value) {
    this.view.setState((prev) => {
      const items = [...(prev.object[stateKey] || [])];
      items[index] = { ...items[index], quantity: value };

      return {
        object: {
          ...prev.object,
          [stateKey]: items,
        },
      };
    });
  }

  handleSelectOne(e, id) {
    const { selectedRawIds } = this.view.state;
    const updated = e.target.checked
      ? [...selectedRawIds, id]
      : selectedRawIds.filter((itemId) => itemId !== id);
    this.view.setState({ selectedRawIds: updated });
  }

  handleSelectAll(e) {
    const checked = e.target.checked;
    const allIds = (this.view.state.object.raw_materials || []).map(
      (item) => item.id,
    );
    this.view.setState({
      selectedRawIds: checked ? allIds : [],
    });
  }

  handleDeleteSelected() {
    const { selectedRawIds, object } = this.view.state;
    const updated = (object.raw_materials || []).filter(
      (item) => !selectedRawIds.includes(item.id),
    );

    this.view.setState({
      object: {
        ...object,
        raw_materials: updated,
      },
      selectedRawIds: [], // reset after delete
    });
  }

  mergeSelectedRawMaterials(selectedItems) {
    this.view.setState((prev) => ({
      object: {
        ...prev.object,
        raw_materials: this.mergeItemsByQuantity(
          prev.object.raw_materials || [],
          selectedItems,
        ),
      },
    }));
  }

  mergeItemsByQuantity(existing, incoming) {
    const itemMap = new Map(existing.map((item) => [item.id, item]));

    incoming.forEach((item) => {
      const existingItem = itemMap.get(item.id);
      itemMap.set(item.id, {
        ...item,
        quantity: existingItem?.quantity ?? item.quantity ?? "",
      });
    });

    return Array.from(itemMap.values());
  }

  mergeSelectedComponents(selectedItems) {
    this.view.setState((prev) => {
      const prevObject = prev.object || {};

      return {
        object: {
          ...prevObject,
          otherComponents: this.mergeComponentsByStockQuantity(
            prevObject.components || [],
            selectedItems,
          ),
          // raw_materials: this.mergeScaledRawMaterials(
          //   prevObject.raw_materials || [],
          //   selectedItems,
          // ),
        },
      };
    });
  }

  mergeComponentsByStockQuantity(existing, incoming) {
    const componentMap = new Map(existing.map((comp) => [comp.id, comp]));

    incoming.forEach((comp) => {
      const existingComp = componentMap.get(comp.id);
      componentMap.set(comp.id, {
        ...comp,
        stock_quantity:
          existingComp?.stock_quantity ?? comp.stock_quantity ?? "",
      });
    });

    return Array.from(componentMap.values());
  }

  mergeScaledRawMaterials(existingRawMaterials, selectedComponents) {
    const rawMap = new Map(existingRawMaterials.map((rm) => [rm.id, rm]));

    selectedComponents.forEach((comp) => {
      const scale = this.calculateComponentScale(comp);

      (comp.raw_materials || []).forEach((raw) => {
        const existingRaw = rawMap.get(raw.id);
        const scaledQty = Math.ceil(parseFloat(raw.quantity || 0) * scale);

        rawMap.set(raw.id, {
          ...raw,
          quantity: parseFloat(raw.quantity || 0) + scaledQty,
          stock_quantity:
            existingRaw?.stock_quantity ?? raw.stock_quantity ?? "",
        });
      });
    });

    return Array.from(rawMap.values());
  }

  calculateComponentScale(component) {
    const baseQty = parseFloat(component.component_quantity || 0);
    const userQty = parseFloat(component.quantity || 0);
    return baseQty ? userQty / baseQty : 1;
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = { include: ["raw_materials", "raw_materials.uom", "uom"] };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async save() {
    const collection = this.view.getCollectionName();

    if (this.object.id) {
      this.change.id = this.object.id;
    } else {
      this.change.acl = this.view.getAcl();
    }

    const rawMaterials = computePartialCosts(
      this.view.state.object.raw_materials || [],
    );
    const otherComponentWithRaw = this.view.state.object.otherComponents;
    const rawMaterialsCost = computeTotalCost(rawMaterials);
    const otherComponentsCost = computeOtherComponentsCost(
      otherComponentWithRaw || [],
      this.view.state.object.quantity || 0,
    );
    const addRawComponentCost = Number(rawMaterialsCost) + otherComponentsCost;

    this.change.raw_materials = rawMaterials;
    this.change.otherComponents = otherComponentWithRaw;
    this.change.cost = addRawComponentCost.toFixed(2);
    this.change.rawCost = String(rawMaterialsCost);
    this.change.componentCost = String(otherComponentsCost);

    try {
      await this.upsertUseCase.execute(collection, this.change);
      this.change = {};
      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              this.view.navigateBack();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      throw error;
    }
  }

  async submit() {
    try {
      this.view.submitting();
      dialog.close();
      await this.save();
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default ComponentsFormPresenter;
